import { useTranslation } from 'react-i18next';

import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Alert, Badge, Box, IconButton, Snackbar, Tab, Tabs, Tooltip } from '@mui/material';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

import { ErrorState, PageContainer } from '@/shared/ui';

import { usePromotions } from '../../hooks';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import { useToast } from '../../lib/useToast';

const TABS = [
  { path: '/promo/calendar', labelKey: 'workspace.tabs.calendar' },
  { path: '/promo/promotions', labelKey: 'workspace.tabs.promotions' },
  { path: '/promo/coupons', labelKey: 'workspace.tabs.coupons' },
  { path: '/promo/sets', labelKey: 'workspace.tabs.sets' },
  { path: '/promo/budgets', labelKey: 'workspace.tabs.budgets' },
  { path: '/promo/approvals', labelKey: 'workspace.tabs.approvals' },
  { path: '/promo/simulator', labelKey: 'workspace.tabs.simulator' },
  { path: '/promo/audit', labelKey: 'workspace.tabs.audit' },
  { path: '/promo/settings', labelKey: 'workspace.tabs.settings' },
] as const;

/**
 * "Promo" workspace shell (plan-011 §7) — a scrollable `Tabs` strip bound to sub-routes (not
 * `useState`, unlike `PricingPage`) so the approval queue and budget alerts stay deep-linkable.
 * One `useToast` + `Snackbar` here feeds every tab panel via an `onResult` prop.
 */
export function PromoWorkspacePage() {
  const { t } = useTranslation('promotions');
  const location = useLocation();
  const { canView } = usePromoPermissions();
  const { toast, success, error: notifyError, clear } = useToast();
  const onResult = (severity: 'success' | 'error' | 'info' | 'warning', message: string) =>
    severity === 'success' ? success(message) : notifyError(message);

  // A small, cheap query (size 1) just to read `totalElements` for the Approvals tab badge —
  // visible to every viewer so a manager sees their submission is waiting even without `approve`.
  const pendingCount = usePromotions({ status: 'PENDING_APPROVAL', page: 0, size: 1 }, { enabled: canView });
  const badgeCount = pendingCount.data?.totalElements ?? 0;

  const activeTab = TABS.find((tab) => location.pathname.startsWith(tab.path))?.path ?? '/promo/calendar';

  if (!canView) {
    return (
      <PageContainer title={t('workspace.title')}>
        <ErrorState title={t('accessRestricted.title')} message={t('accessRestricted.message')} />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={t('workspace.title')}
      subtitle={t('workspace.subtitle')}
      actions={
        <Tooltip title={t('workspace.settingsTooltip')}>
          <IconButton
            component={RouterLink}
            to="/promo/settings"
            aria-label={t('workspace.settingsTooltip')}
            sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
          >
            <SettingsRoundedIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs value={activeTab} variant="scrollable" allowScrollButtonsMobile>
          {TABS.map((tab) => (
            <Tab
              key={tab.path}
              value={tab.path}
              component={RouterLink}
              to={tab.path}
              label={
                tab.path === '/promo/approvals' && badgeCount > 0 ? (
                  <Badge
                    color="warning"
                    badgeContent={badgeCount}
                    max={99}
                    aria-label={t('workspace.pendingApprovalBadgeAria', { count: badgeCount })}
                  >
                    <Box component="span" sx={{ pr: 1.5 }}>
                      {t(tab.labelKey)}
                    </Box>
                  </Badge>
                ) : (
                  t(tab.labelKey)
                )
              }
            />
          ))}
        </Tabs>
      </Box>

      <Outlet context={{ onResult }} />

      <Snackbar open={toast !== null} autoHideDuration={5000} onClose={clear} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
